import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { Layout, Divider, Spin, Table, Input, Button, message, Collapse } from "antd";
import { FileTextOutlined, SearchOutlined, UserOutlined, DollarOutlined, AppstoreOutlined } from "@ant-design/icons";
import CommonDivWidget from "components/CommonDivWidget/CommonDivWidget";
import ClubBoxWidget from "components/ClubBoxWidget/ClubBoxWidget";
import { apiGetReportNames, apiRunReport } from "services/dashboardService";
import { getReportFormatter, hasCustomFormatter } from "./reportFormatters";
import PrintableReport from "./PrintableReport";
import ForteSettlementReport from "./ForteSettlementReport";
import ReactToPrint from "react-to-print";
import './CustomReportsPage.css';

const { Content, Sider } = Layout;

// Synthetic report object — pinned at the top of Financial Reports (category 2)
const FORTE_SETTLEMENT_REPORT = {
    report_name:  'Forte Settlement Report',
    display_group: 2,
    _pinned: true,
};

// Helper function to detect if a row is a total/summary row
const isTotalRow = (row) => {
    const firstValue = Object.values(row)[0];
    if (!firstValue) return false;
    const str = String(firstValue).toUpperCase();
    return str.includes('TOTAL') || str.includes('SUBTOTAL') || str.includes('SUM') || 
           str === '---' || str.startsWith('===');
};

// Helper function to detect if a row is a header row
const isHeaderRow = (row) => {
    const values = Object.values(row);
    // Check if all values are strings and none are numbers
    return values.every(v => typeof v === 'string' && isNaN(v) && v.trim() !== '');
};

// Helper function to format currency values
const formatCurrency = (value) => {
    if (value == null || value === '') return value;
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(num);
};

// Helper function to detect if a column contains currency
const isCurrencyColumn = (columnName, sampleValues) => {
    if (!columnName || !sampleValues) return false;
    const name = columnName.toLowerCase();
    // Check column name keywords (avoid generic words like 'credit' that appear as values)
    if (name.includes('amount') || name.includes('price') || name.includes('fee') ||
        name.includes('cost') || name.includes('total') || name.includes('balance') ||
        name.includes('deposit') || name.includes('payment') || name.includes('charge')) {
        return true;
    }
    // Check if ALL non-null sample values are strictly numeric (no partial matches like "04/01/2026")
    const nonNullValues = sampleValues.filter(v => v != null && v !== '');
    if (nonNullValues.length === 0) return false;
    const numericValues = nonNullValues.filter(v => !isNaN(v) && String(v).trim() !== '');
    return numericValues.length === nonNullValues.length;
};

function CustomReportsPage() {
    const userDataStore = useSelector((x) => x.userDataStore);
    const appDataStore = useSelector((x) => x.appDataStore);

    const [isBusy, setIsBusy] = useState(false);
    const [reportNamesList, setReportNamesList] = useState([]);
    const [selectedReport, setSelectedReport] = useState(null);
    const [inputs, setInputs] = useState({});
    const [results, setResults] = useState([]);
    const [allResultSets, setAllResultSets] = useState([]); // Store all result sets
    const [printHeader, setPrintHeader] = useState("");
    const [searchKey, setSearchKey] = useState("");
    const printComponentRef = useRef(null);

    const getReportNames = useCallback(() => {
        const params = {
            "token": userDataStore.token,
            "club_id": appDataStore.club.id,
            "registration_id": appDataStore.curRegistration.ID,
            "user_id": userDataStore.user.ID,
        };

        setIsBusy(true);
        apiGetReportNames(params)
            .then((res) => {
                if (res && res.report_names) {
                    setReportNamesList(res.report_names);
                } else {
                    setReportNamesList([]);
                }
                setIsBusy(false);
            })
            .catch((err) => {
                message.error(err);
                setIsBusy(false);
            });
    }, [appDataStore.club, appDataStore.curRegistration, userDataStore.token, userDataStore.user]);

    useEffect(() => {
        // Clear results and selection whenever the registration changes
        setSelectedReport(null);
        setInputs({});
        setResults([]);
        setAllResultSets([]);
        setSearchKey("");
        if (appDataStore.club && appDataStore.curRegistration) {
            getReportNames();
        }
    }, [appDataStore.club, appDataStore.curRegistration, getReportNames]); // getReportNames was added as a dependency

    useEffect(() => {
        // Set the print header to include the logo, centered.
        const headerHtml = `
      <div style="text-align: center;">
        <img src="/assets/images/logo-large.png" alt="Company Logo" style="max-width: 250px; margin-bottom: 20px;" />
      </div>`;
        setPrintHeader(headerHtml);
    }, []);

    const handleReportClick = (report) => {
        try {
            setSelectedReport(report);
            setInputs({});
            setResults([]);
        } catch (error) {
            console.error('Error in handleReportClick:', error);
            message.error('Error selecting report: ' + error.message);
        }
    };

    // Group reports by category based on display_group integer
    const categorizedReports = useMemo(() => {
        const categories = {
            1: { name: 'Registration Reports', reports: [] },
            2: { name: 'Financial Reports', reports: [] },
            3: { name: 'Other Reports', reports: [] }
        };

        reportNamesList.forEach((report) => {
            const displayGroup = report.display_group || 3; // Default to 3 (Other Reports)
            if (categories[displayGroup]) {
                categories[displayGroup].reports.push(report);
            } else {
                categories[3].reports.push(report); // Fallback to Other Reports
            }
        });

        return categories;
    }, [reportNamesList]);

    // Filter reports based on search key
    const filteredReports = useMemo(() => {
        if (!searchKey || searchKey.trim() === '') {
            return categorizedReports;
        }

        const filtered = {
            1: { name: 'Registration Reports', reports: [] },
            2: { name: 'Financial Reports', reports: [] },
            3: { name: 'Other Reports', reports: [] }
        };

        const searchLower = searchKey.toLowerCase();

        Object.keys(categorizedReports).forEach((key) => {
            filtered[key].reports = categorizedReports[key].reports.filter((report) =>
                report.report_name.toLowerCase().includes(searchLower)
            );
        });

        return filtered;
    }, [categorizedReports, searchKey]);

    const handleInputChange = (field, value) => {
        setInputs((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = () => {
        if (!selectedReport) {
            message.warning("Please select a report first.");
            return;
        }

        const inputFields = selectedReport.input_names ? selectedReport.input_names.split(",") : [];

        // Build params as an object (not URLSearchParams) for proper axios handling
        const params = {
            token: userDataStore.token,
            registration_id: appDataStore.curRegistration.ID,
            report_sp: selectedReport.report_sp,
            input1: appDataStore.curRegistration.ID,
        };

        // Then add user inputs as input2, input3...
        for (let i = 1; i < inputFields.length; i++) {
            const trimmedFieldName = inputFields[i].trim();
            const inputValue = inputs[trimmedFieldName] || "";
            console.log(`[CustomReport] input${i+1} (${trimmedFieldName}): "${inputValue}"`);
            params[`input${i + 1}`] = inputValue;
        }
        
        console.log('[CustomReport] Final params:', params);

        setIsBusy(true);
        apiRunReport(params)
            .then((res) => {
                if (res && res.results) {
                    // Check if we have multiple result sets (array of arrays)
                    if (Array.isArray(res.results) && res.results.length > 0) {
                        // If first element is an array, we have multiple result sets
                        if (Array.isArray(res.results[0])) {
                            setAllResultSets(res.results);
                            // Use the last result set as the main display (usually the data)
                            setResults(res.results[res.results.length - 1] || []);
                        } else {
                            // Single result set
                            setAllResultSets([res.results]);
                            setResults(res.results);
                        }
                    } else {
                        setResults([]);
                        setAllResultSets([]);
                    }
                    
                    if (res.results.length === 0 || (Array.isArray(res.results[0]) && res.results[0].length === 0)) {
                        message.info("No results found.");
                    }
                } else {
                    setResults([]);
                    message.info("No results found.");
                }
                setIsBusy(false);
            })
            .catch((err) => {
                message.error("Failed to fetch report data.");
                setIsBusy(false);
            });
    };


    const handleExport = () => {
        if (!results.length) {
            message.warning("No data to export.");
            return;
        }

        // Filter out columns where all values are null/empty
        const keys = Object.keys(results[0] || {});
        const keysWithValues = keys.filter(key =>
            results.some(row => row[key] !== null && row[key] !== undefined && row[key] !== '')
        );
        const filteredResults = results.map(row => {
            const filtered = {};
            keysWithValues.forEach(key => { filtered[key] = row[key]; });
            return filtered;
        });

        const worksheet = XLSX.utils.json_to_sheet(filteredResults);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Results");

        const today = new Date();
        const filename = `CustomReports_${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}.xlsx`;

        XLSX.writeFile(workbook, filename);
    };

    // Generate columns for the results table with enhanced formatting
    const baseColumns = useMemo(() => {
        if (results.length === 0) {
            return [];
        }

        // Check if this report has custom formatting
        const customFormatter = getReportFormatter(selectedReport?.report_name);
        const columnConfig = customFormatter?.columnConfig || {};

        // Collect sample values for each column to detect types
        const sampleValues = {};
        results.slice(0, 10).forEach(row => {
            Object.keys(row).forEach(key => {
                if (!sampleValues[key]) sampleValues[key] = [];
                sampleValues[key].push(row[key]);
            });
        });

        return Object.keys(results[0]).map((key, idx, arr) => {
            // Get custom column config if exists
            const customColConfig = columnConfig[key] || {};
            
            // Find first non-null value to determine column type for sorting
            const firstNonNullValue = results.find(item => item[key] != null)?.[key];
            const isNumericColumn = typeof firstNonNullValue === 'number';
            const isCurrency = customColConfig.isCurrency || isCurrencyColumn(key, sampleValues[key]);

            return {
                title: key,
                dataIndex: key,
                key,
                sorter: (a, b) => {
                    const valA = a[key];
                    const valB = b[key];

                    // Keep nulls/undefined at the top
                    if (valA == null && valB == null) return 0;
                    if (valA == null) return -1;
                    if (valB == null) return 1;

                    if (isNumericColumn) {
                        return valA - valB;
                    }

                    // Case-insensitive string sort
                    return String(valA).localeCompare(String(valB), undefined, {
                        numeric: true,
                        sensitivity: "base",
                    });
                },
                ellipsis: true,
                // Use custom formatter if provided, otherwise use smart defaults
                render: (value, record) => {
                    // Use custom formatter if available
                    if (customColConfig.format) {
                        return customColConfig.format(value);
                    }
                    // Default formatting
                    if (isCurrency && value != null) {
                        return formatCurrency(value);
                    }
                    // Right-align numbers
                    if (isNumericColumn && value != null) {
                        return <span style={{ display: 'block', textAlign: 'right' }}>{value}</span>;
                    }
                    return value;
                },
                align: customColConfig.align || (isCurrency || isNumericColumn ? 'right' : 'left'),
                width: customColConfig.width || (idx < arr.length - 1 ? 150 : undefined),
            };
        });
    }, [results, selectedReport?.report_name]);

    return (
        <CommonDivWidget>
            <Spin spinning={isBusy}>
                <Layout style={{ background: "#fff", padding: 16 }}>
                    <ClubBoxWidget />
                    <Divider style={{ borderTop: "4px solid var(--mainColor)" }} />

                    <Layout>
                        <Sider width={300} style={{ background: "#f7f7f7", padding: 16, paddingTop: 16 }}>
                            <div style={{ marginBottom: 16 }}>
                                <Input
                                    placeholder="Search reports..."
                                    prefix={<SearchOutlined />}
                                    value={searchKey}
                                    onChange={(e) => setSearchKey(e.target.value)}
                                    allowClear
                                    style={{ borderRadius: 6 }}
                                />
                            </div>
                            <div className="report-results-container">
                                <Collapse
                                    defaultActiveKey={['1', '2', '3']}
                                    ghost
                                    expandIconPosition="right"
                                    style={{ background: 'transparent' }}
                                >
                                    {[1, 2, 3].map((categoryKey) => {
                                        const category = filteredReports[categoryKey];

                                        // Financial Reports always shows (has pinned item); others hide when empty during search
                                        if (category.reports.length === 0 && categoryKey !== 2 && searchKey) {
                                            return null;
                                        }
                                        if (category.reports.length === 0 && categoryKey === 2 && searchKey &&
                                            !FORTE_SETTLEMENT_REPORT.report_name.toLowerCase().includes(searchKey.toLowerCase())) {
                                            return null;
                                        }

                                        // Select icon based on category
                                        const categoryIcon = categoryKey === 1 ? <UserOutlined style={{ marginRight: 8 }} /> :
                                                           categoryKey === 2 ? <DollarOutlined style={{ marginRight: 8 }} /> :
                                                           <AppstoreOutlined style={{ marginRight: 8 }} />;

                                            // For Financial Reports, prepend the pinned settlement report
                                        const pinnedItems = categoryKey === 2 ? [FORTE_SETTLEMENT_REPORT] : [];
                                        const allItems = [...pinnedItems, ...category.reports];

                                        const renderReportItem = (report) => {
                                            const isSelected = selectedReport?.report_name === report.report_name;
                                            return (
                                                <div
                                                    key={report.id || report.report_name}
                                                    onClick={() => handleReportClick(report)}
                                                    style={{
                                                        padding: '8px 12px',
                                                        cursor: 'pointer',
                                                        borderRadius: 4,
                                                        marginBottom: 4,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
                                                        border: isSelected ? '1px solid #1890ff' : '1px solid transparent',
                                                        transition: 'all 0.2s',
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!isSelected) e.currentTarget.style.backgroundColor = '#f5f5f5';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                                    }}
                                                >
                                                    <FileTextOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                                                    <span style={{ fontSize: 13 }}>{report.report_name}</span>
                                                </div>
                                            );
                                        };

                                        return (
                                            <Collapse.Panel
                                                header={
                                                    <span style={{ fontWeight: 500, fontSize: 14 }}>
                                                        {categoryIcon}
                                                        {category.name}
                                                    </span>
                                                }
                                                key={categoryKey}
                                            >
                                                {allItems.length === 0 ? (
                                                    <div style={{ padding: '8px 0', color: '#999', fontSize: 12 }}>
                                                        No reports found
                                                    </div>
                                                ) : (
                                                    <>
                                                        {pinnedItems.map(renderReportItem)}
                                                        {pinnedItems.length > 0 && category.reports.length > 0 && (
                                                            <div style={{ borderTop: '1px solid #e8e8e8', margin: '4px 0 8px' }} />
                                                        )}
                                                        {category.reports.map(renderReportItem)}
                                                    </>
                                                )}
                                            </Collapse.Panel>
                                        );
                                    })}
                                </Collapse>
                            </div>
                        </Sider>

                        <Content style={{ marginLeft: 16 }}>
                            {!selectedReport ? (
                                <div>
                                    <h2 className="report-title">Custom Reports</h2>
                                    <div
                                        style={{
                                            marginTop: 24,
                                            width: "100%",
                                            padding: "40px",
                                            textAlign: "center",
                                            backgroundColor: "#fff",
                                            borderRadius: "8px",
                                            border: "1px solid #eee",
                                        }}
                                    >
                                        <Table
                                            dataSource={Array.from({ length: 10 }, (_, i) => ({ key: i }))}
                                            columns={[
                                                { title: '', dataIndex: 'col1', key: 'col1', width: '20%' },
                                                { title: '', dataIndex: 'col2', key: 'col2', width: '20%' },
                                                { title: '', dataIndex: 'col3', key: 'col3', width: '20%' },
                                                { title: '', dataIndex: 'col4', key: 'col4', width: '20%' },
                                                { title: '', dataIndex: 'col5', key: 'col5', width: '20%' },
                                            ]}
                                            pagination={false}
                                            bordered
                                            rowClassName={(record, index) => index % 2 === 1 ? 'table-row-light' : ''}
                                        />
                                    </div>
                                </div>
                            ) : selectedReport._pinned ? (
                                <ForteSettlementReport />
                            ) : (
                                <div>
                                    <h2 className="report-title">Custom Reports</h2>

                                    {selectedReport.input_names && selectedReport.input_names.trim() !== '' && (
                                        <div style={{ marginBottom: 24, marginTop: 25 }}>
                                            <h4>Inputs for {selectedReport.report_name}</h4>
                                            {selectedReport.input_names
                                                .split(",")
                                                .filter(name => name && name.trim())
                                                .slice(1)
                                                .map((inputName) => (
                                                    <Input
                                                        key={inputName.trim()}
                                                        placeholder={`Enter ${inputName.trim()}`}
                                                        value={inputs[inputName.trim()] || ""}
                                                        onChange={(e) => handleInputChange(inputName.trim(), e.target.value)}
                                                        style={{ marginBottom: 12, width: 300 }}
                                                    />
                                                ))}
                                            <div>
                                                <Button type="primary" onClick={handleSubmit}>
                                                    Submit
                                                </Button>
                                                <Button onClick={handleExport} style={{ marginLeft: 10 }} disabled={!results.length}>
                                                    Export
                                                </Button>
                                                <ReactToPrint
                                                    trigger={() => (
                                                        <Button style={{ marginLeft: 10 }} disabled={!results.length}>
                                                            Print
                                                        </Button>
                                                    )}
                                                    content={() => printComponentRef.current}
                                                    documentTitle={selectedReport ? `${selectedReport.report_name} - Custom Report` : "Custom Report"}
                                                    pageStyle={`
                                                        @media print {
                                                            @page {
                                                                size: landscape;
                                                                margin: 20mm;
                                                            }
                                                            .ant-table {
                                                                font-size: 10px;
                                                            }
                                                            h2 {
                                                                text-align: center;
                                                                font-size: 18px;
                                                                margin-bottom: 20px;
                                                            }
                                                        }`}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {results?.length > 0 ? (
                                        <div>
                                            {/* Display header result sets if present (like report period info) */}
                                            {allResultSets.length > 1 && allResultSets.slice(0, -1).map((resultSet, setIdx) => (
                                                resultSet.length > 0 && (
                                                    <div
                                                        key={`header-${setIdx}`}
                                                        style={{
                                                            padding: '16px',
                                                            backgroundColor: '#f0f5ff',
                                                            borderRadius: '8px',
                                                            marginBottom: '16px',
                                                            border: '1px solid #d6e4ff'
                                                        }}
                                                    >
                                                        {Object.entries(resultSet[0]).map(([key, value]) => (
                                                            <div key={key} style={{ marginBottom: 8 }}>
                                                                <strong>{key}:</strong> {value}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )
                                            ))}
                                            
                                            {/* Main data table */}
                                            <div
                                                style={{
                                                    width: "100%",
                                                    overflowX: "auto",
                                                    padding: "12px",
                                                    backgroundColor: "#fff",
                                                    borderRadius: "8px",
                                                    border: "1px solid #eee",
                                                    marginTop: 24,
                                                }}
                                            >
                                                {(() => {
                                                    const customFormatter = getReportFormatter(selectedReport?.report_name);
                                                    const tableProps = {
                                                        dataSource: results,
                                                        columns: baseColumns,
                                                        pagination: customFormatter?.pagination ?? false,
                                                        rowKey: (record, index) => index,
                                                        scroll: customFormatter?.scroll ?? { x: 'max-content' },
                                                        bordered: true,
                                                        size: customFormatter?.size ?? 'middle',
                                                        rowClassName: (record, index) => {
                                                            // Use custom formatter if available
                                                            if (customFormatter?.getRowStyle) {
                                                                const rowStyle = customFormatter.getRowStyle(record);
                                                                return rowStyle.className || '';
                                                            }
                                                            // Default styling
                                                            if (isTotalRow(record)) {
                                                                return 'table-row-total';
                                                            }
                                                            return index % 2 === 1 ? 'table-row-light' : '';
                                                        },
                                                    };

                                                    return (
                                                        <Table {...tableProps} />
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            style={{
                                                marginTop: 24,
                                                width: "100%",
                                                padding: "40px",
                                                textAlign: "center",
                                                backgroundColor: "#fff",
                                                borderRadius: "8px",
                                                border: "1px solid #eee",
                                                color: "#999",
                                                fontSize: "16px",
                                            }}
                                        >
                                            No results found.
                                        </div>
                                    )}
                                </div>
                            )}
                        </Content>
                    </Layout>
                    {/* Hidden component for printing. */}
                    <div style={{ display: 'none' }}>
                        <PrintableReport
                            ref={printComponentRef}
                            results={results}
                            columns={baseColumns}
                            reportName={selectedReport?.report_name}
                            printHeader={printHeader}
                                                    inputValues={selectedReport?.input_names ? selectedReport.input_names.split(",").slice(1).map((field) => inputs[field.trim()] || "").filter(v => v) : []}
                        />
                    </div>
                </Layout>
            </Spin>
        </CommonDivWidget>
    );
}

export default CustomReportsPage;
